import type { PermissionResource } from '../graphql/permissionResources/types'

// The permission grid's tick rules: form logic about what to SAVE. Whether a screen or button shows
// for the viewer is never decided here; the API computes it (lib/viewerPermissions.ts).

export const ACTIONS = ['get', 'post', 'patch', 'delete'] as const
export type Action = (typeof ACTIONS)[number]
export type PermissionMap = Record<string, Set<Action>>

export function isAction(s: string): s is Action {
  return (ACTIONS as readonly string[]).includes(s)
}

// Within one resource: a stronger action implies the weaker ones, and removing a weaker action removes the stronger ones that depend on it
const CASCADE_ENABLE: Record<Action, Action[]> = {
  delete: ['patch', 'post', 'get'],
  patch: ['post', 'get'],
  post: ['get'],
  get: [],
}

const CASCADE_DISABLE: Record<Action, Action[]> = {
  get: ['post', 'patch', 'delete'],
  post: ['patch', 'delete'],
  patch: ['delete'],
  delete: [],
}

function change(
  map: PermissionMap,
  resources: PermissionResource[],
  key: string,
  action: Action,
  on: boolean,
  // Resource/action pairs already handled in this toggle: a catalog whose `requires` form a cycle
  // (A requires B, B requires A) would otherwise recurse forever
  visited: Set<string> = new Set(),
) {
  const id = `${key}:${action}:${on}`
  if (visited.has(id)) {
    return
  }
  visited.add(id)
  const affected = on ? [action, ...CASCADE_ENABLE[action]] : [action, ...CASCADE_DISABLE[action]]
  const set = new Set(map[key] ?? [])
  for (const a of affected) {
    if (on) {
      set.add(a)
    } else {
      set.delete(a)
    }
  }
  map[key] = set
  // Ticking also ticks the same action on what this resource requires; unticking also unticks
  // it on every resource that requires this one
  const next = on
    ? (resources.find(r => r.key === key)?.requires ?? [])
    : resources.filter(r => r.requires.includes(key)).map(r => r.key)
  for (const other of next) {
    for (const a of affected) {
      change(map, resources, other, a, on, visited)
    }
  }
}

// The grid after the user toggles one checkbox
export function toggleAction(
  permissions: PermissionMap,
  resources: PermissionResource[],
  key: string,
  action: Action,
): PermissionMap {
  const next: PermissionMap = { ...permissions }
  change(next, resources, key, action, !permissions[key]?.has(action))
  return next
}

// Completes what a resource's `requires` demand, so a saved set never breaks a rule (an existing
// user may have been saved before the rule existed). Each action of a resource that has `requires`
// is completed as if ticked: View comes with Create, and the same action (with its cascade) on
// every required resource. Resources without `requires` stay exactly as saved.
export function satisfyRequirements(
  permissions: PermissionMap,
  resources: PermissionResource[],
): PermissionMap {
  const next: PermissionMap = { ...permissions }
  for (const resource of resources) {
    if (resource.requires.length === 0) {
      continue
    }
    for (const action of permissions[resource.key] ?? []) {
      change(next, resources, resource.key, action, true)
    }
  }
  return next
}
